import { useCallback, useEffect, useState } from "react";

import {
  resendEmailVerificationOTP,
  sendEmailVerificationOTP,
  verifyEmailOTP,
} from "api/services/jobs";

import { apiError } from "../api";
import { demoApi } from "../lib/demo";
import { normaliseEmail } from "./steps";

const RESEND_AFTER_SECONDS = 30;

function useCountdown() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);
  return [seconds, setSeconds];
}

// Email ownership check: send a 4-digit code, verify it. `verifiedEmail` is
// the address that passed; it stops counting the moment the email changes.
const realApi = {
  sendCode: sendEmailVerificationOTP,
  resendCode: resendEmailVerificationOTP,
  verifyCode: verifyEmailOTP,
};

export default function useEmailVerification({ jobId, demo = false, initialVerifiedEmail, onVerified }) {
  const api = demo ? demoApi : realApi;
  const [verifiedEmail, setVerifiedEmail] = useState(initialVerifiedEmail || null);
  const [codeSentTo, setCodeSentTo] = useState(null);
  const [busy, setBusy] = useState(null); // "sending" | "verifying" | null
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useCountdown();
  // Bumped after a rejected code so the boxes clear and refocus.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setVerifiedEmail(initialVerifiedEmail || null);
  }, [initialVerifiedEmail]);

  const send = useCallback(
    async (email, { resend = false } = {}) => {
      const address = normaliseEmail(email);
      setBusy("sending");
      setError("");
      try {
        const call = resend ? api.resendCode : api.sendCode;
        await call({ email: address, job: jobId });
        setCodeSentTo(address);
        setCooldown(RESEND_AFTER_SECONDS);
        return true;
      } catch (e) {
        setError(apiError(e, "We couldn't send the code. Check the email address and try again."));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [api, jobId, setCooldown],
  );

  const verify = useCallback(
    async (code) => {
      if (!codeSentTo) return false;
      setBusy("verifying");
      setError("");
      try {
        await api.verifyCode({ email: codeSentTo, otp: code });
        setVerifiedEmail(codeSentTo);
        setCodeSentTo(null);
        onVerified?.(codeSentTo);
        return true;
      } catch (e) {
        setError(apiError(e, "That code didn't work. Check it and try again."));
        setAttempt((n) => n + 1);
        return false;
      } finally {
        setBusy(null);
      }
    },
    [api, codeSentTo, onVerified],
  );

  const reset = useCallback(() => {
    setVerifiedEmail(null);
    setCodeSentTo(null);
    setError("");
    onVerified?.(null);
  }, [onVerified]);

  return {
    demo,
    verifiedEmail,
    isVerified: (email) => Boolean(verifiedEmail) && verifiedEmail === normaliseEmail(email),
    codeSentTo,
    attempt,
    busy,
    error,
    cooldown,
    send,
    verify,
    reset,
    cancelCode: () => {
      setCodeSentTo(null);
      setError("");
    },
  };
}
